<template>
  <div class="table-footer">
    <div class="table-footer__left">
      <v-select
        :style="selectStyle"
        :value="itemsPerPage"
        :items="normalizedOptions"
        hide-details
        dense
        outlined
        height="32px"
        class="table-footer__select"
        @change="val => $emit('update:itemsPerPage', val)"
      ></v-select>
      <span class="table-footer__count">{{ rangeText }}</span>
    </div>

    <div class="table-footer__center" :style="centerStyle">
      <div v-if="compact" class="table-footer__compact">
        <v-btn icon small :disabled="page <= 1" @click="$emit('update:page', page - 1)">
          <v-icon>{{ icons.arrowLeft }}</v-icon>
        </v-btn>
        <span class="table-footer__page">Page {{ page }} of {{ pageCount }}</span>
        <v-btn icon small :disabled="page >= pageCount" @click="$emit('update:page', page + 1)">
          <v-icon>{{ icons.arrowRight }}</v-icon>
        </v-btn>
      </div>
      <v-pagination
        v-else
        :key="pageCount"
        elevation="0"
        :value="page"
        :length="pageCount"
        :total-visible="totalVisible"
        :prev-icon="icons.arrowLeft"
        :next-icon="icons.arrowRight"
        @input="val => $emit('update:page', val)"
      ></v-pagination>
    </div>

    <div class="table-footer__right"></div>
  </div>
</template>

<script>
import { mdiArrowRight, mdiArrowLeft } from '@mdi/js';

export default {
  name: 'TableFooter',
  props: {
    page: { type: Number, default: 1 },
    pageCount: { type: Number, default: 1 },
    itemsPerPage: { type: Number, default: 10 },
    total: { type: Number, default: 0 },
    itemsPerPageOptions: {
      type: Array,
      default: () => [5, 10, 15, 20],
    },
  },
  data() {
    return {
      icons: {
        arrowRight: mdiArrowRight,
        arrowLeft: mdiArrowLeft,
      },
    };
  },
  computed: {
    // Prihvaća i [5, 10] i [{ text, value }] oblik zbog postojećih pozivatelja.
    normalizedOptions() {
      return this.itemsPerPageOptions.map(option =>
        typeof option === 'object' && option !== null
          ? option
          : { text: option === -1 ? 'All' : `${option} Rows`, value: option }
      );
    },
    rangeText() {
      if (!this.total) return '0 of 0';
      if (this.itemsPerPage === -1) return `1-${this.total} of ${this.total}`;
      const start = (this.page - 1) * this.itemsPerPage + 1;
      const end = Math.min(this.page * this.itemsPerPage, this.total);
      return `${start}-${end} of ${this.total}`;
    },
    // Ispod 5 vidljivih Vuetify izbaci srednji dio liste i broj trenutne
    // stranice se uopce ne prikaze, pa numericka varijanta ne ide ispod 7.
    totalVisible() {
      return this.$vuetify.breakpoint.mdAndDown ? 7 : 10;
    },
    compact() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    // Vuetify racuna maxButtons kao (sirina roditelja - 96) / 42, a roditelj je
    // grid kolona koja se siri po sadrzaju. Bez ovoga mjerenje moze kolabirati.
    centerStyle() {
      return this.compact ? {} : { 'min-width': `${96 + this.totalVisible * 42}px` };
    },
    selectStyle() {
      return this.$vuetify.theme.dark
        ? { 'background-color': '#212121' }
        : { 'background-color': '#ffffff' };
    },
  },
};
</script>

<style lang="scss" scoped>
.table-footer {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px 16px;
  padding: 12px 16px;
  border-top: 1px solid var(--v-borderColor-base);
}

.table-footer__left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.table-footer__center {
  min-width: 0;
}

.table-footer__select {
  flex: 0 1 150px;
  max-width: 150px;
  font-size: 12px;
  min-height: 1em;
}

.table-footer__count {
  font-family: 'Roboto';
  font-size: 12px;
  line-height: 14px;
  white-space: nowrap;
  color: #a7a7a7;
}

.table-footer__compact {
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-footer__page {
  font-family: 'Roboto';
  font-size: 13px;
  white-space: nowrap;
}

::v-deep .v-pagination {
  flex-wrap: nowrap;
}

// Ispod md nema dovoljno mjesta za tri kolone: sve ide u jedan centrirani stupac.
@media (max-width: 959px) {
  .table-footer {
    grid-template-columns: 1fr;
    justify-items: center;
  }

  .table-footer__right {
    display: none;
  }
}

@media (max-width: 599px) {
  .table-footer__select {
    flex-basis: 120px;
    max-width: 120px;
  }
}
</style>
